import { createSignal, Show } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { useI18n } from "@/providers/i18n-provider";
import { api } from "@/lib/api";
import { sharedApi } from "@bso/shared";
import { msg } from "@lingui/core/macro";
import { CheckCircle2, KeyRound, Loader2 } from "lucide-solid";

/**
 * Cambio de contraseña desde el perfil.
 *
 * Pide la contraseña actual + la nueva (con confirmación en el cliente). El
 * server valida la actual, y al cambiarla cierra las sesiones de los demás
 * dispositivos y avisa por email. Tras el éxito se limpia el formulario.
 */
export function ChangePasswordSection() {
  const { t } = useI18n();
  const [current, setCurrent] = createSignal("");
  const [next, setNext] = createSignal("");
  const [confirm, setConfirm] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [done, setDone] = createSignal(false);
  const [busy, setBusy] = createSignal(false);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next() !== confirm()) {
      setError(t(msg`Passwords do not match`));
      return;
    }
    void (async () => {
      setBusy(true);
      try {
        const res = await sharedApi.changePassword(api, {
          current_password: current(),
          new_password: next(),
        });
        // Los tipos OpenAPI generados no declaran respuesta de error para este
        // endpoint (res.error es siempre undefined según el contrato), pero
        // mantenemos la guarda: en runtime un 401 sí llega como error.
        // oxlint-disable-next-line typescript/no-unnecessary-condition -- guarda defensiva ante errores de runtime no tipados
        if (res.error) {
          // 401 → la contraseña actual es incorrecta; otros → error genérico.
          setError(t(msg`Your current password is incorrect.`));
          return;
        }
        setCurrent("");
        setNext("");
        setConfirm("");
        setDone(true);
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Card>
      <CardHeader class="pb-2">
        <div class="flex items-center gap-2">
          <KeyRound class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Change password`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <form class="space-y-4" onSubmit={submit}>
          <div class="space-y-2">
            <Label for="current-password">{t(msg`Current password`)}</Label>
            <TextField
              id="current-password"
              type="password"
              autocomplete="current-password"
              required
              value={current()}
              onInput={(e) => setCurrent(e.currentTarget.value)}
            />
          </div>
          <div class="space-y-2">
            <Label for="new-password">{t(msg`New password`)}</Label>
            <TextField
              id="new-password"
              type="password"
              autocomplete="new-password"
              required
              minLength={8}
              value={next()}
              onInput={(e) => setNext(e.currentTarget.value)}
            />
          </div>
          <div class="space-y-2">
            <Label for="confirm-password">{t(msg`Confirm new password`)}</Label>
            <TextField
              id="confirm-password"
              type="password"
              autocomplete="new-password"
              required
              minLength={8}
              value={confirm()}
              onInput={(e) => setConfirm(e.currentTarget.value)}
            />
          </div>

          <Show when={error()}>
            <p class="text-sm text-destructive" data-testid="change-password-error">
              {error()}
            </p>
          </Show>
          <Show when={done()}>
            <p
              class="flex items-center gap-2 text-sm font-medium text-primary"
              data-testid="change-password-success"
            >
              <CheckCircle2 class="h-4 w-4" />
              {t(msg`Password updated. Other devices have been signed out.`)}
            </p>
          </Show>

          <Button
            type="submit"
            class="w-full rounded-2xl gap-2 h-12"
            disabled={busy()}
            data-testid="change-password-submit"
          >
            <Show when={busy()}>
              <Loader2 class="h-5 w-5 animate-spin" />
            </Show>
            {t(msg`Update password`)}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
